import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Skeleton,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import { Activity, DollarSign, TrendingUp, Zap } from "lucide-react";
import { useState } from "react";
import { usageService } from "../../services/admin";

export default function AdminUsage() {
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const { data: analytics, isLoading } = useQuery({
		queryKey: ["admin-usage", startDate, endDate],
		queryFn: () =>
			usageService.getUsageAnalytics({
				startDate: startDate || undefined,
				endDate: endDate || undefined,
			}),
	});

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Usage Analytics
					</h1>
					<p className="text-muted-foreground mt-2">
						Platform-wide usage metrics and analytics
					</p>
				</div>
			</div>

			{/* Date Range */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Date Range</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="start-date">Start Date</Label>
							<Input
								id="start-date"
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="end-date">End Date</Label>
							<Input
								id="end-date"
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Stats Cards */}
			{isLoading ? (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					{[1, 2, 3, 4].map((i) => (
						<Card key={i}>
							<CardHeader className="pb-2">
								<Skeleton className="h-4 w-24" />
							</CardHeader>
							<CardContent>
								<Skeleton className="h-8 w-16 mb-2" />
								<Skeleton className="h-3 w-32" />
							</CardContent>
						</Card>
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<Activity className="w-4 h-4" />
								Total Requests
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{analytics?.totalRequests.toLocaleString() || 0}
							</div>
							<p className="text-xs text-muted-foreground">All time</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<TrendingUp className="w-4 h-4" />
								Cache Hit Rate
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{analytics?.cacheHitRate.toFixed(1) || "0.0"}%
							</div>
							<p className="text-xs text-muted-foreground">
								{analytics?.cacheHits || 0} cache hits
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<Zap className="w-4 h-4" />
								Tokens Saved
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{analytics?.totalTokensSaved.toLocaleString() || 0}
							</div>
							<p className="text-xs text-muted-foreground">
								{analytics?.savingsPercentage.toFixed(1) || "0.0"}% savings
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<DollarSign className="w-4 h-4" />
								Cost Saved
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								${analytics?.totalCostSaved.toFixed(2) || "0.00"}
							</div>
							<p className="text-xs text-muted-foreground">Total savings</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Detailed Metrics */}
			{analytics && (
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Detailed Metrics</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<Label className="text-xs text-muted-foreground">
									Total Tokens Used
								</Label>
								<p className="text-2xl font-bold mt-1">
									{analytics.totalTokensUsed.toLocaleString()}
								</p>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Total Tokens Saved
								</Label>
								<p className="text-2xl font-bold mt-1">
									{analytics.totalTokensSaved.toLocaleString()}
								</p>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Savings Percentage
								</Label>
								<p className="text-2xl font-bold mt-1">
									{analytics.savingsPercentage.toFixed(1)}%
								</p>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Total Actual Cost
								</Label>
								<p className="text-2xl font-bold mt-1">
									${analytics.totalActualCost.toFixed(2)}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
